import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { EmptyState } from '@/components/ui/EmptyState'
import { Input } from '@/components/ui/Input'
import {
  useAdminGetWhitelist,
  useAdminAddToWhitelist,
  useAdminRemoveFromWhitelist,
  getAdminGetWhitelistQueryKey,
} from '@/api/generated/v2-admin/v2-admin'
import type { WhitelistItemResponse, WhitelistAddResponse } from '@/api/schemas'
import { adminLoginHref } from '@/lib/adminApi'
import { formatDateKST } from '@/lib/utils'
import { createFileRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Plus, ShieldCheck, Trash2 } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'

export const Route = createFileRoute('/admin/whitelist')({
  component: WhitelistManagement,
})

function WhitelistManagement() {
  const queryClient = useQueryClient()
  const [mallIdsText, setMallIdsText] = useState('')
  const [cohort, setCohort] = useState<string>('')

  // 삭제 확인 Dialog 상태
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    mallId: string
  } | null>(null)

  // Orval Query 훅: 화이트리스트 목록 조회
  const { data: whitelistRaw, isLoading } = useAdminGetWhitelist()
  const whitelists = (whitelistRaw ?? []) as WhitelistItemResponse[]

  // 쿼리 무효화 헬퍼
  const invalidateWhitelist = () => {
    queryClient.invalidateQueries({ queryKey: getAdminGetWhitelistQueryKey() })
  }

  // Orval Mutation 훅: 화이트리스트 벌크 추가
  const addMutation = useAdminAddToWhitelist({
    mutation: {
      onSuccess: (response) => {
        // onSuccess는 HTTP 200일 때만 호출되므로 성공 타입으로 캐스팅
        const result = response as unknown as WhitelistAddResponse
        setMallIdsText('')
        // 기수는 유지 (같은 기수로 여러 번 등록할 수 있으므로)
        invalidateWhitelist()

        // 결과 메시지 표시
        if (result.added.length > 0 && result.skipped.length > 0) {
          toast.success(
            `${result.added.length}개 추가 완료 (${result.skipped.length}개 중복 건너뜀)`,
          )
        } else if (result.added.length > 0) {
          toast.success(`${result.added.length}개 카페24 ID가 추가되었어요.`)
        } else {
          toast('모두 이미 등록된 카페24 ID입니다.', { icon: 'i' })
        }
      },
      onError: (err) => {
        // 인증 만료 체크
        if (err && typeof err === 'object' && 'status' in err && err.status === 401) {
          toast.error('인증이 만료되었어요. 다시 로그인해주세요.')
          // 로그인 후 보던 화면으로 복귀
          window.location.href = adminLoginHref(
            window.location.pathname + window.location.search,
          )
        } else {
          const message = err && typeof err === 'object' && 'detail' in err
            ? String(err.detail)
            : '카페24 ID 추가에 실패했어요.'
          toast.error(message)
        }
      },
    },
  })

  // Orval Mutation 훅: 화이트리스트에서 제거
  const removeMutation = useAdminRemoveFromWhitelist({
    mutation: {
      onSuccess: () => {
        invalidateWhitelist()
        setDeleteTarget(null)
        toast.success('카페24 ID가 화이트리스트에서 제거되었어요.')
      },
      onError: (err) => {
        setDeleteTarget(null)
        if (err && typeof err === 'object' && 'status' in err && err.status === 401) {
          toast.error('인증이 만료되었어요. 다시 로그인해주세요.')
          // 로그인 후 보던 화면으로 복귀
          window.location.href = adminLoginHref(
            window.location.pathname + window.location.search,
          )
        } else {
          toast.error('카페24 ID 제거에 실패했어요.')
        }
      },
    },
  })

  const addMallIds = (e: React.FormEvent) => {
    e.preventDefault()

    // 콤마, 줄바꿈, 공백으로 분리 후 빈 값 제거
    const mallIds = mallIdsText
      .split(/[,\n\s]+/)
      .map((id) => id.trim())
      .filter(Boolean)

    if (mallIds.length === 0) return

    const cohortNum = cohort ? Number(cohort) : null
    addMutation.mutate({
      data: { mall_ids: mallIds, cohort: cohortNum },
    })
  }

  const removeMallId = (id: string) => {
    removeMutation.mutate({ whitelistId: id })
  }

  // 기수별 그룹핑 (테이블 표시용)
  const distinctCohorts = [
    ...new Set(whitelists.map((w) => w.cohort).filter(Boolean)),
  ].sort((a, b) => (a as number) - (b as number))

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <h1 className="text-xl font-bold text-on-surface mb-6">
        화이트리스트 관리
      </h1>

      {/* 카페24 ID 추가 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>카페24 ID 등록</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={addMallIds} className="space-y-3">
            <div className="flex gap-3 items-end">
              {/* 카페24 ID 입력 (여러 개 가능) */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-on-surface mb-1">
                  카페24 ID
                </label>
                <Input
                  value={mallIdsText}
                  onChange={(e) => setMallIdsText(e.target.value)}
                  placeholder="콤마로 여러 개 입력 가능 (예: shop1, shop2)"
                  required
                  disabled={addMutation.isPending}
                />
              </div>

              {/* 기수 입력 */}
              <div className="w-24">
                <label className="block text-sm font-medium text-on-surface mb-1">
                  기수
                </label>
                <Input
                  type="number"
                  min={1}
                  value={cohort}
                  onChange={(e) => setCohort(e.target.value)}
                  placeholder="선택"
                  disabled={addMutation.isPending}
                />
              </div>

              <Button
                type="submit"
                disabled={addMutation.isPending}
                loading={addMutation.isPending}
              >
                <Plus className="h-4 w-4 mr-1" />
                등록
              </Button>
            </div>

            <p className="text-sm text-on-surface-variant">
              * 화이트리스트에 등록된 카페24 ID만 서비스에 접근할 수 있습니다.
            </p>
          </form>
        </CardContent>
      </Card>

      {/* 등록된 카페24 ID 목록 */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              등록된 카페24 ID 목록 ({whitelists.length}개)
            </CardTitle>
            {distinctCohorts.length > 0 && (
              <div className="flex gap-1.5">
                {distinctCohorts.map((c) => (
                  <Badge key={c} variant="secondary" className="text-xs">
                    {c}기
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </CardHeader>

        {isLoading ? (
          <CardContent>
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="ml-2 text-on-surface-variant">로딩 중...</span>
            </div>
          </CardContent>
        ) : whitelists.length === 0 ? (
          <CardContent>
            <EmptyState
              icon={<ShieldCheck className="h-12 w-12" />}
              title="등록된 카페24 ID가 없어요."
              description="위에서 카페24 ID를 추가해 주세요."
            />
          </CardContent>
        ) : (
          <Table>
            <TableHeader className="bg-surface">
              <TableRow>
                <TableHead className="px-6">카페24 ID</TableHead>
                <TableHead className="px-6">기수</TableHead>
                <TableHead className="px-6">등록일</TableHead>
                <TableHead className="px-6 text-right">작업</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {whitelists.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="px-6 whitespace-nowrap">
                    <div className="font-medium text-on-surface">
                      {item.mall_id}
                    </div>
                  </TableCell>
                  <TableCell className="px-6 whitespace-nowrap">
                    {item.cohort ? (
                      <Badge variant="secondary">{item.cohort}기</Badge>
                    ) : (
                      <span className="text-outline-variant">-</span>
                    )}
                  </TableCell>
                  <TableCell className="px-6 whitespace-nowrap">
                    <div className="text-on-surface-variant">
                      {formatDateKST(item.created_at)}
                    </div>
                  </TableCell>
                  <TableCell className="px-6 whitespace-nowrap text-right">
                    <Button
                      variant="danger-outline"
                      size="sm"
                      onClick={() =>
                        setDeleteTarget({ id: item.id, mallId: item.mall_id })
                      }
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                      삭제
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* 삭제 확인 Dialog */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        size="sm"
      >
        <DialogContent>
          <DialogHeader onClose={() => setDeleteTarget(null)}>
            <DialogTitle>카페24 ID 삭제</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-on-surface-variant mb-6">
            정말 <strong>"{deleteTarget?.mallId}"</strong>를 화이트리스트에서
            삭제하시겠습니까?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              취소
            </Button>
            <Button
              variant="danger"
              onClick={() => deleteTarget && removeMallId(deleteTarget.id)}
            >
              삭제
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
